import Reactor from 'data/reactor';
import { Store, toImmutable, Immutable } from 'nuclear-js';

import { subjects, QUADRANT_CHART_DEFAULT_ORIENTATION } from 'resources/common-values';
import RuntimeActions from 'data/app-runtime/actions';
import RosterActions from 'data/roster/actions';
import StudentActions from 'data/student/actions';
import StudentGetters from 'data/student/getters';
import PartnerGetters from 'data/partner/getters';
import StudentUtils from 'utils/students';
import DataUtils from 'data/utils';

const initialSubject = {
    bid: 1,
    name: 'Mathematics',
    shortName: 'mathematics',
    disciplineName: 'Mathematics',
    description: 'Mathematics',
    order: 1,
};

const clearAlert = Immutable.Map({
    messageContent: null,
    isActive: false,
});

export default Store({
    getInitialState() {
        return toImmutable({
            allowAdvanceStudent: false,
            displaySettings: {
                animationIsRunning: false,
                appLevelCssClassName: '',
                gradeDropdownOpen: false,
                expandedModule: null,
                openHelpBoxId: '',
                OpenModalDialogId: '',
                preferredSubject: initialSubject,
                selectedSubject: initialSubject,
                showHighlights: true,
                singleStudentSearchRendering: false,
                studentListOpen: false,
            },
            filters: {
                // will be structure like this:
                //      { <subject>:  {
                //                        <ImmutableList of goals>
                //                        <ImmutableMap holding default comparison period>
                //                    }
                //        quadrantChartRotation: default/rotated
                //      }
                quadrantChartRotation: QUADRANT_CHART_DEFAULT_ORIENTATION,
                selectedGoals: Immutable.Map(),
                selectedComparisonPeriod: Immutable.Map(),
            },
            initialLoadDone: false,
            modalReferences: {}, // list of methods for opening modal dialogs
            modules: loadModuleData(),
            pageAlert: {
                isActive: false,
                messageContent: null,
                actionStatus: '',
            },
            popoverTermNumber: null,
            studentTransitionAction: null, // null, 'left', 'right'
            subjects: subjects,
            unsavedDataState: {
                // group them together so they can be retrieved together
                modifier: null, // the component/location that modified unsavedData
                unsavedData: false,
            },
            userAction: '',
        });
    },

    initialize() {
        this.on(RuntimeActions.CHANGE_SELECTED_SUBJECT, changeSelectedSubject);
        this.on(RuntimeActions.EXPAND_MODULE, expandModule);
        this.on(RuntimeActions.TOGGLE_HELPBOX_POPOVER, storeOpenHelpBoxId);
        this.on(RuntimeActions.SET_STUDENT_TRANSITION, (state, payload) =>
            setStudentTransition(state, payload.movingTo, payload.userAction)
        );
        this.on(RosterActions.SELECT_STUDENT_BID, clearDisplaySettings);
        this.on(RuntimeActions.ADVANCE_TO_ADJACENT_STUDENT, advanceToAdjacentStudent);
        this.on(RuntimeActions.CLOSE_GRADE_DROPDOWN, closeGradeDropdown);
        this.on(RuntimeActions.TOGGLE_STUDENT_LIST_POPOVER, toggleStudentListPopover);
        this.on(RuntimeActions.CLEAR_STATE, (state) => this.getInitialState());
        this.on(RuntimeActions.SELECT_INSTRUCTIONAL_AREAS, storeSelectedGoals);
        this.on(RuntimeActions.SELECT_COMPARISON_PERIOD, storeSelectedComparisonPeriod);
        this.on(RuntimeActions.SET_UNSAVED_DATA, setUnsavedData);
        this.on(RuntimeActions.SET_USER_ACTION, setUserAction);
        this.on(RuntimeActions.REGISTER_MODAL, registerModal);
        this.on(RuntimeActions.TOGGLE_HIGHLIGHTS, toggleHighlights);
        this.on(StudentActions.CLEAR_STUDENT_DATA, clearStudentData);
        this.on(StudentActions.CHANGE_SELECTED_TERM_NUMBER, changeSelectedTermNumber);
        this.on(RuntimeActions.SET_PAGE_ALERT, setPageAlert);
        this.on(RuntimeActions.SINGLE_STUDENT_SEARCH_RENDERING, setSingleStudentSearchRendering);
        this.on(RuntimeActions.RESET_DISPLAY_SETTINGS, resetDisplaySettingsForSingleStudentSearch);
        this.on(RuntimeActions.ALLOW_ADVANCE_STUDENT, allowAdvanceStudent);
        this.on(RuntimeActions.INITIAL_LOAD_DONE, initialLoadDone);
        this.on(RuntimeActions.COMPLETE_TRANSITION, completeTransition);
        this.on(RuntimeActions.SET_ANIMATION_IS_RUNNING, setAnimationIsRunning);
    },
});

function changeSelectedTermNumber(state, payload) {
    return state.withMutations((s) => {
        // The comparison period dropdown depends on what term we're currently viewing
        s.setIn(['filters', 'selectedComparisonPeriod'], Immutable.Map());
        s.set('userAction', 'changing-content');
        // when the term number changes, update the selected subject if necessary
        const preferredSubject = s.getIn(['displaySettings', 'preferredSubject']);
        const currentStudentTestEvents = Reactor.evaluate(StudentGetters.testEventsForStudentBid(payload.studentBid));
        //Find partner's current term, if it exists
        const currentTerm = Reactor.evaluate(PartnerGetters.currentTerm);
        const subject = StudentUtils.getSubjectToSelect(
            preferredSubject,
            payload.termNumber,
            currentStudentTestEvents,
            currentTerm
        );
        // if the new subject does not match the currently selected subject, update the state
        if (subject.get('bid', -2) !== s.getIn(['displaySettings', 'selectedSubject', 'bid'], -1)) {
            let transitionInfo = Immutable.Map({
                isTransitionInProgress: true,
                transitionType: 'selectSubject',
                updatePreferredSubject: payload.updatePreferredSubject,
                newSubject: subject,
            });
            s.setIn(['displaySettings', 'transitionInfo'], transitionInfo);
            s.set('userAction', 'changing-subject');
        } else {
            s.setIn(['pageAlert'], clearAlert);
            s.set('userAction', '');
        }
    });
}

function clearStudentData(state) {
    return state.withMutations((s) => {
        s.setIn(['unsavedDataState', 'unsavedData'], false);
        s.setIn(['unsavedDataState', 'modifier'], null);
        s.set('userAction', '');
        s.setIn(['pageAlert'], clearAlert);
    });
}

function registerModal(state, payload) {
    if (!payload || !payload.modalName || !payload.openModalFunc) {
        throw new Error(
            `registerModal: must pass payload with modalName and openModalFunc.  payload was ${JSON.stringify(payload)}`
        );
    }

    return state.withMutations((s) => {
        s.setIn(['modalReferences', payload.modalName], payload.openModalFunc);
        s.setIn(['pageAlert'], clearAlert);
    });
}

function setUnsavedData(state, payload) {
    return state.withMutations((s) => {
        s.setIn(['unsavedDataState', 'unsavedData'], payload.unsavedYn);
        s.setIn(['unsavedDataState', 'modifier'], payload.modifier); // the name of the thing that modified unsavedData
    });
}

function closeGradeDropdown(state) {
    return state.setIn(['displaySettings', 'gradeDropdownOpen'], false);
}

function setUserAction(state, action) {
    return state.set('userAction', action);
}

export function changeSelectedSubject(state, payload) {
    if (payload.delayStateUpdateUntilTransitionComplete) {
        let transitionInfo = Immutable.Map({
            isTransitionInProgress: true,
            transitionType: 'selectSubject',
            updatePreferredSubject: payload.updatePreferredSubject,
            newSubject: payload.newSubject,
        });

        return state.withMutations((s) => {
            s.setIn(['displaySettings', 'transitionInfo'], transitionInfo);
        });
    } else {
        return updateSubjectState(state, Immutable.Map(payload));
    }
}

function updateModuleState(state, payload) {
    return state.withMutations((s) => {
        s.setIn(['displaySettings', 'expandedModule'], payload.get('moduleToExpand'));
        s.set('popoverTermNumber', payload.get('termNumber'));
        s.setIn(['pageAlert'], clearAlert);
    });
}

function updateSubjectState(state, payload) {
    return state.withMutations((s) => {
        s.setIn(['displaySettings', 'selectedSubject'], payload.get('newSubject'));
        s.set('userAction', '');
        s.setIn(['pageAlert'], clearAlert);
        if (payload.get('updatePreferredSubject')) {
            s.setIn(['displaySettings', 'preferredSubject'], payload.get('newSubject'));
            s.setIn(['pageAlert'], clearAlert);
        }
    });
}

function completeTransition(state) {
    let transitionInfo = state.getIn(['displaySettings', 'transitionInfo']);

    if (!transitionInfo || transitionInfo.get('isTransitionInProgress') === false) {
        /*
          This is common when transitioning students, because the Growth Over Time chart has a transition in
          and a transition out, and both of them will call completeTransition. This is harmless, but we
          definitely want to put a log in the dispatch to keep the app from acting too mysteriously.
          */
        if (__DEV__) {
            console.debug('Completed a transition while there was no transition in progress');
        }
        return state;
    }

    switch (transitionInfo.get('transitionType')) {
        case 'expandModule':
            state = updateModuleState(state, transitionInfo);
            return state.setIn(['displaySettings', 'transitionInfo', 'isTransitionInProgress'], false);
        case 'selectSubject':
            state = updateSubjectState(state, transitionInfo);
            return state.setIn(['displaySettings', 'transitionInfo', 'isTransitionInProgress'], false);
        default:
            console.error('Completing transaction of unknown type', transitionInfo.get('transitionType'));
            return state;
    }
}

function expandModule(state, payload) {
    if (payload.delayStateUpdateUntilTransitionComplete) {
        let transitionInfo = Immutable.Map({
            isTransitionInProgress: true,
            transitionType: 'expandModule',
            moduleToExpand: payload.moduleToExpand,
            termNumber: payload.termNumber,
        });

        return state.withMutations((s) => {
            s.setIn(['displaySettings', 'transitionInfo'], transitionInfo);
        });
    } else {
        state = updateModuleState(state, Immutable.Map(payload));
        return state.set('userAction', payload.moduleToExpand == null ? 'closing-module' : 'expanding-module');
    }
}

function clearDisplaySettings(state) {
    return state.withMutations((s) => {
        s.set('studentTransitionAction', null);
        s.setIn(['pageAlert'], clearAlert);
        s.set('userAction', '');
    });
}

function setStudentTransition(state, movingTo = null, action = '') {
    return state.withMutations((s) => {
        s.set('studentTransitionAction', movingTo);
        s.setIn(['pageAlert'], clearAlert);
        s.set('userAction', action);
    });
}

function advanceToAdjacentStudent(state) {
    const adjacentStudent = DataUtils.findAdjacentStudent();
    const newCurrentBid = adjacentStudent ? adjacentStudent.get('studentBid', null) : null;
    return setStudentTransition(state, null, 'changing-content');
}

function loadModuleData() {
    return [
        {
            name: 'Comparisons',
            shortName: 'comparisons',
        },
        {
            name: 'Instructional Areas',
            shortName: 'instructional-areas',
        },
        {
            name: 'Growth Goals',
            shortName: 'growth-goals',
        },
        {
            name: 'Growth Over Time',
            shortName: 'growth-over-time',
        },
    ];
}

function storeOpenHelpBoxId(state, payload) {
    return state.withMutations((s) => {
        s.setIn(['displaySettings', 'openHelpBoxId'], payload.openHelpBoxId);
        s.setIn(['displaySettings', 'appLevelCssClassName'], payload.appLevelCssClassName);
    });
}

function toggleStudentListPopover(state, showStudentList) {
    return state.setIn(['displaySettings', 'studentListOpen'], showStudentList);
}

export function storeSelectedGoals(state, payload) {
    if (!payload || !payload.goals || !payload.subject) {
        return state;
    }

    return state.withMutations((s) => {
        // goals should be immutable list (not null)
        s.setIn(
            ['filters', 'selectedGoals', payload.subject],
            payload.goals.sortBy((s1) => s1.get('score'))
        );
    });
}

export function storeSelectedComparisonPeriod(state, payload) {
    if (!payload || !payload.selectedPeriod || !payload.subject) {
        return state;
    }

    return state.withMutations((s) => {
        s.setIn(['filters', 'selectedComparisonPeriod', payload.subject], payload.selectedPeriod);
    });
}

export function toggleHighlights(state) {
    return state.updateIn(['displaySettings', 'showHighlights'], (show) => !show);
}

export function setAnimationIsRunning(state, isRunning) {
    return state.withMutations((s) => {
        s.setIn(['displaySettings', 'animationIsRunning'], isRunning);
    });
}

export function setPageAlert(state, pageAlert) {
    return state.withMutations((s) => {
        s.set('pageAlert', pageAlert);
    });
}

export function setSingleStudentSearchRendering(state, singleStudentSearchRendering) {
    return state.setIn(['displaySettings', 'singleStudentSearchRendering'], singleStudentSearchRendering);
}

export function resetDisplaySettingsForSingleStudentSearch(state) {
    const displaySettings = this.getInitialState().get('displaySettings');
    state.setIn(['displaySettings'], displaySettings);
    return state.setIn(['displaySettings', 'singleStudentSearchRendering'], true);
}

export function allowAdvanceStudent(state, bool) {
    return state.set('allowAdvanceStudent', bool);
}

export function initialLoadDone(state, bool) {
    return state.set('initialLoadDone', bool);
}
